import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

interface WorkstationOption {
  id: string;
  display_name: string;
}

const schema = z.object({
  name: z.string().trim().min(1).max(80),
  workstation_id: z.string().optional(),
});

export type CashRegisterFormValues = z.infer<typeof schema>;

interface CashRegisterFormDialogProps {
  title: string;
  initial_values?: Partial<CashRegisterFormValues>;
  workstation_options: readonly WorkstationOption[];
  submitting: boolean;
  onSubmit: (values: CashRegisterFormValues) => Promise<void>;
  onCancel: () => void;
}

export function CashRegisterFormDialog({
  title,
  initial_values,
  workstation_options,
  submitting,
  onSubmit,
  onCancel,
}: CashRegisterFormDialogProps): JSX.Element {
  const { t } = useTranslation();
  const form = useForm<CashRegisterFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: initial_values?.name ?? '',
      workstation_id: initial_values?.workstation_id ?? '',
    },
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="w-full max-w-md">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void form.handleSubmit((values) => onSubmit(values))(event);
          }}
          noValidate
        >
          <CardHeader>
            <CardTitle className="font-display font-bold tracking-tight">{title}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="register_name">{t('settings_ops.cash_registers.name')}</Label>
              <Input id="register_name" maxLength={80} {...form.register('name')} />
              {form.formState.errors.name ? (
                <p className="text-sm text-destructive">{form.formState.errors.name.message}</p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="register_workstation">{t('settings_ops.workstation')}</Label>
              <select
                id="register_workstation"
                {...form.register('workstation_id')}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">{t('settings_ops.workstation_none')}</option>
                {workstation_options.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.display_name}
                  </option>
                ))}
              </select>
            </div>
          </CardContent>
          <CardFooter className="justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? t('common.loading') : t('common.save')}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
